import UserInfoHOC from '#components/HOC/UserInfoHOC'
import Button from '#components/a/Button'
import Headline from '#components/a/Headline'
import Status from '#components/a/Status'
import Text from '#components/a/Text'
import AuthorInfo from '#components/m/AuthorInfo'
import ControlsGroup from '#components/m/ControlsGroup'
import DropdownSelect from '#components/m/DropdownSelect'
import Tooltip from '#components/m/Tooltip'
import WorkingHourLight from '#components/svg/icons/WorkingHourLight'
import useUserModuleSettings from '#hooks/useUserModuleSettings'
import Api from '#services/api'
import {isDraftOrRejected, statusTypes} from 'ag-common/tools/fileStatus'
import nachweisNumber from 'ag-common/tools/nachweisNumber'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'
import actions from 'redux-auto'
import NachweisDetailAbteilungInput from './NachweisDetailAbteilungInput'
import NachweisDetailHeaderModal from './NachweisDetailHeaderModal'

export function NachweisDetailHeader({
  isNewNachweis,
  nachweis,
  userInfo,
  hasChanges,
  isSaving,
  disablePrintButton,
  handleEinreichenButton,
  handleFreigabeZurueckziehenButton,
  onNachweisAbteilungChange,
  onNachweisAbteilungNameChange,
  permissions,
  suppressEmptyAbteilungInputError,
  hasTaetigkeitWithText,
}) {
  const [dateRangeString, setDateRangeString] = useState(undefined)
  const [errorMessage, setErrorMessage] = useState(undefined)
  const [isCreatingPdf, setIsCreatingPdf] = useState(false)
  const [abteilungOptions, setAbteilungOptions] = useState([])
  const [selectedAbteilung, setSelectedAbteilung] = useState()
  const [preselectedAbteilung, setPreselectedAbteilung] = useState(undefined)
  const [superiorNames, setSuperiorNames] = useState([])
  const {showModule} = useUserModuleSettings()
  const hasVersetzungsplan = showModule('versetzungsplan')

  const {nummer, status, lastStatusChange, userId, abteilungsname, summeTaetigkeitenMinuten, summeTaetigkeitenStunden} =
    nachweis
  const numberDisplay = nummer
  const modifier = 'edit'
  const isNachweisEditable = permissions.nachweis.edit && isDraftOrRejected(nachweis.status)
  const showAbteilungsDropdown = isNachweisEditable && hasVersetzungsplan
  const showAbteilungsInput = isNachweisEditable && !hasVersetzungsplan
  const showVerticalRule = !isNachweisEditable || showAbteilungsDropdown || showAbteilungsInput

  const dateAndAbteilungModifier = isNachweisEditable ? 'nachweisdetail__header--date-and-abteilung--is-editable' : ''

  useEffect(() => {
    setDateRangeString(nachweisNumber.getDateRangeStringByNachweis(nachweis, userInfo))
  })

  const loadAbteilungeForUser = async () => {
    const abteilungen = await Api.getUserAbteilungenForAzubi(dateRangeString)
    const options = abteilungen.map(a => ({label: a.name, value: a.id}))
    setAbteilungOptions(options)

    const preselectedAbteilungFromApi = await Api.automaticallySelectCurrentAbteilungForAzubi(dateRangeString)
    setPreselectedAbteilung(preselectedAbteilungFromApi)
  }

  const loadNamesForAzubi = async () => {
    const superiorNames = await Api.getSuperiorNamesForAzubi(
      nachweis?.abteilungsId ?? selectedAbteilung,
      dateRangeString,
    )
    setSuperiorNames(superiorNames)
  }

  useEffect(() => {
    if (permissions.nachweis.edit && hasVersetzungsplan) {
      loadAbteilungeForUser()
    }
  }, [permissions.nachweis.edit, hasVersetzungsplan])

  useEffect(() => {
    if (permissions.nachweis.edit) {
      loadNamesForAzubi()
    }
  }, [isNewNachweis, nachweis.abteilungsId])

  useEffect(() => {
    if (isNachweisEditable) {
      const selectedOption = abteilungOptions.find(o => o.value === nachweis.abteilungsId)
      setSelectedAbteilung(selectedOption)
      if (preselectedAbteilung && !selectedAbteilung) {
        const selectedOption = abteilungOptions?.find(o => o.value === preselectedAbteilung.id)
        setSelectedAbteilung(selectedOption)
        onNachweisAbteilungChange({
          id: selectedOption.value,
          name: selectedOption.name,
        })
      }
    }
  }, [abteilungOptions, nachweis.abteilungsId, preselectedAbteilung])

  const onAbteilungSelectionChange = option => {
    onNachweisAbteilungChange({
      id: option.value,
      name: option.name,
    })
  }

  const onPrintNachweis = async () => {
    setIsCreatingPdf(true)
    try {
      await Api.printNachweisById(nachweis)
    } catch (e) {
      actions.error.writeError({error: e})
    } finally {
      setIsCreatingPdf(false)
    }
  }

  const renderPrintButton = () => {
    if (!disablePrintButton) {
      return (
        <Button
          id="nachweis_print_button"
          disabled={isCreatingPdf}
          text={i18n.t('export_print_buttontext')}
          event={() => onPrintNachweis(nachweis)}
          variant="secondary"
          modifiers={['print']}
        />
      )
    }
    return null
  }

  const renderEinreichenButton = () => {
    if (permissions.nachweis.edit && isDraftOrRejected(nachweis.status)) {
      return !hasTaetigkeitWithText ? (
        <Tooltip
          triggerAction="hover"
          modifier="dark small"
          trigger={
            <NachweisDetailHeaderModal
              id="nachweis_einreichen_button"
              trigger={i18n.t('nachweis_einreichen_button')}
              confirmCallback={handleEinreichenButton}
              confirmText={i18n.t('nachweis_einreichen_button')}
              disabled={!hasTaetigkeitWithText}>
              <Headline modifier="article" importance={2} text={i18n.t('nachweis_einreichen_modal_header')} />
              <Text modifier="small" text={i18n.t('nachweis_einreichen_modal_text')} />
            </NachweisDetailHeaderModal>
          }
          placement="left">
          <Text modifier="in-tooltip" text={i18n.t('nachweis_einreichen_disabled_tooltip')} />
        </Tooltip>
      ) : (
        <NachweisDetailHeaderModal
          id="nachweis_einreichen_button"
          trigger={i18n.t('nachweis_einreichen_button')}
          confirmCallback={handleEinreichenButton}
          confirmText={i18n.t('nachweis_einreichen_button')}
          disabled={!hasTaetigkeitWithText}>
          <Headline modifier="article" importance={2} text={i18n.t('nachweis_einreichen_modal_header')} />
          <Text modifier="small" text={i18n.t('nachweis_einreichen_modal_text')} />

          {superiorNames.map((name, index) => {
            return <Text key={index} modifier="small--no-margin-bottom" text={'- ' + name} />
          })}
        </NachweisDetailHeaderModal>
      )
    }
    return null
  }

  const renderFreigabeZurueckziehenButton = () => {
    if (permissions.nachweis.reset && nachweis.status === statusTypes.confirmed) {
      return (
        <NachweisDetailHeaderModal
          id="nachweis_freigabe_zurueckziehen_button"
          trigger={i18n.t('nachweis_freigabe_zurueckziehen_button')}
          confirmCallback={handleFreigabeZurueckziehenButton}
          confirmText={i18n.t('nachweis_freigabe_zurueckziehen_button')}
          secondary>
          <Headline modifier="article" importance={2} text={i18n.t('nachweis_freigabe_zurueckziehen_modal_header')} />
          <Text modifier="small" text={i18n.t('nachweis_freigabe_zurueckziehen_modal_text')} />
        </NachweisDetailHeaderModal>
      )
    }
    return null
  }

  return (
    <div className="nachweisdetail__header">
      <div className={`nachweisdetail__header--date-and-abteilung ${dateAndAbteilungModifier}`}>
        <Headline data-testid="nachweis-date-range" importance={6} text={dateRangeString} />
        {showVerticalRule && <div className="nachweisdetail__verticalrule" />}
        <div data-testid="nachweisheader-abteilungsname">
          {!isNachweisEditable && <Headline importance={6} modifier="shy" text={abteilungsname} />}
          {showAbteilungsDropdown && (
            <div className="abteilung-select">
              <DropdownSelect
                selectedOption={selectedAbteilung}
                options={abteilungOptions}
                handleSelect={onAbteilungSelectionChange}
                modifier="dark"
                isDisabled={false}
                placeholder={i18n.t('nachweis_abteilung_select_placeholder')}
              />
            </div>
          )}
          {showAbteilungsInput && (
            <NachweisDetailAbteilungInput
              setErrorMessage={setErrorMessage}
              abteilungsname={abteilungsname}
              onAbteilungNameChange={onNachweisAbteilungNameChange}
              suppressEmptyAbteilungError={suppressEmptyAbteilungInputError}
            />
          )}
        </div>
      </div>
      <div className={`nachweisdetail__header--${modifier}`}>
        <Headline
          data-testid="ausbildungsnachweisheadline"
          importance={2}
          modifier="section"
          text={`${i18n.t('ausbildungsnachweiseOverview_headline_single')} ${numberDisplay}`}
        />
        {
          <ControlsGroup modifier="wrapper">
            <ControlsGroup modifier="secondary">{renderPrintButton()}</ControlsGroup>
            <ControlsGroup>
              {renderEinreichenButton()}
              {renderFreigabeZurueckziehenButton()}
            </ControlsGroup>
          </ControlsGroup>
        }
      </div>
      <div className="nachweisdetail__spacer">
        {
          <div className="nachweisdetail__metainfo">
            <Status status={status} canVerify={permissions.nachweis.verify} />
            <AuthorInfo
              isSaving={isSaving}
              hasChanges={hasChanges}
              status={status}
              lastStatusChange={lastStatusChange}
              isAuthor={permissions.nachweis.edit}
              userId={userId}
            />
            <div className="summeTaetigkeiten">
              <Text text="&nbsp;|&nbsp;" />
              <WorkingHourLight />
              <Text text={i18n.t('taetigkeit_gesamt', [summeTaetigkeitenStunden, summeTaetigkeitenMinuten])} />
            </div>
          </div>
        }

        {errorMessage && (
          <div className="nachweisdetail__error">
            <Text text={errorMessage} modifier="error" />
          </div>
        )}
      </div>
    </div>
  )
}

NachweisDetailHeader.propTypes = {
  isNewNachweis: PropTypes.bool,
  isSaving: PropTypes.bool,
  disablePrintButton: PropTypes.bool,
  hasChanges: PropTypes.bool,
  userInfo: PropTypes.object,
  nachweis: PropTypes.object.isRequired,
  ausbildungsnachweise: PropTypes.array,
  handleEinreichenButton: PropTypes.func,
  handleFreigabeZurueckziehenButton: PropTypes.func,
  onNachweisNumberChange: PropTypes.func,
  onNachweisAbteilungChange: PropTypes.func,
  onNachweisAbteilungNameChange: PropTypes.func,
  permissions: PropTypes.object,
  suppressEmptyAbteilungInputError: PropTypes.bool,
  newNachweisNumber: PropTypes.number,
  hasTaetigkeitWithText: PropTypes.bool,
}

export default UserInfoHOC(NachweisDetailHeader, ['authorId'])
